/**
 * Handle service — manages the `handles/{handle}` collection that enforces
 * one-handle-per-user uniqueness via Firestore transactions.
 *
 * Each doc id is the handle in lowercase; the doc body is `{ uid, claimedAt }`.
 * Validation rules: 3–20 chars, lowercase a-z, 0-9, `.`, `_`. Cannot start or
 * end with `.` or `_`, no consecutive dots, no leading digit.
 */
import {
  doc,
  getDoc,
  runTransaction,
  serverTimestamp,
} from "firebase/firestore";

import { db } from "../lib/firebase";

const HANDLES = "handles";

const RE_HANDLE = /^[a-z][a-z0-9._]{1,18}[a-z0-9]$/;

export type HandleValidation =
  | { ok: true }
  | { ok: false; reason: HandleError };

export type HandleError =
  | "tooShort"
  | "tooLong"
  | "invalidChars"
  | "leadingDigit"
  | "edgeSeparator"
  | "consecutiveDots"
  | "taken";

/** Pure validation — does NOT hit the network. */
export function validateHandleFormat(raw: string): HandleValidation {
  const h = raw.trim().toLowerCase();
  if (h.length < 3) return { ok: false, reason: "tooShort" };
  if (h.length > 20) return { ok: false, reason: "tooLong" };
  if (/^\d/.test(h)) return { ok: false, reason: "leadingDigit" };
  if (h.startsWith(".") || h.startsWith("_") || h.endsWith(".") || h.endsWith("_"))
    return { ok: false, reason: "edgeSeparator" };
  if (h.includes("..")) return { ok: false, reason: "consecutiveDots" };
  if (!RE_HANDLE.test(h)) return { ok: false, reason: "invalidChars" };
  return { ok: true };
}

/** Network check — returns false if the handle doc already exists. */
export async function isHandleAvailable(raw: string): Promise<boolean> {
  const h = raw.trim().toLowerCase();
  const snap = await getDoc(doc(db, HANDLES, h));
  return !snap.exists();
}

/**
 * Atomically claim a handle for a uid. Throws if taken.
 * Use this on signup and any future "change handle" flow.
 */
export async function claimHandle(handle: string, uid: string): Promise<void> {
  const h = handle.trim().toLowerCase();
  const v = validateHandleFormat(h);
  if (!v.ok) {
    throw new Error("handle:" + v.reason);
  }
  const ref = doc(db, HANDLES, h);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (snap.exists()) {
      throw new Error("handle:taken");
    }
    tx.set(ref, { uid, claimedAt: serverTimestamp() });
  });
}

/** Look up a uid by handle. Returns null if not registered. */
export async function getUidByHandle(handle: string): Promise<string | null> {
  const h = handle.trim().toLowerCase();
  const snap = await getDoc(doc(db, HANDLES, h));
  return snap.exists() ? (snap.data().uid as string) : null;
}
